<script>
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import pb from '../lib/pocketbase';
    import Swal from 'sweetalert2';

    export let newEntity;
    export let entityName;
    export let currentEntityId;
    export let canModify = false;
    export let canCreate = false;
    // Reusable configuration
    export let galleryCollection;
    export let relationField;
    export let urlField;
    export let fileField = null; // optional PocketBase file field name

    let images = [];
    let isLoading = true;
    let isUploading = false;
    let error = null;
    let newImageUrl = '';

    // Upload input ref
    let headerFileInput;

    function getImageUrl(record) {
        // If urlField and fileField are the same (both 'url'), treat it as a file field
        if (fileField && record?.[fileField]) {
            return pb.files.getUrl(record, record[fileField]);
        }
        // If urlField is different from fileField, treat it as a URL
        if (urlField && urlField !== fileField && record?.[urlField]) {
            return record[urlField];
        }
        return '';
    }

    // No lightbox behavior requested

    function openFileDialog() {
        if (!fileField) return;
        if (headerFileInput) headerFileInput.click();
    }

    // Avoid duplicate initial loads; rely on id changes
    $: if (currentEntityId) {
        // Reload when the entity changes
        loadImages();
    }

    async function loadImages() {
        if (!currentEntityId || !galleryCollection || !relationField) {
            images = [];
            isLoading = false;
            return;
        }

        try {
            isLoading = true;
            error = null;
            const resp = await pb.collection(galleryCollection).getList(1, 200, {
                filter: `${relationField} = \"${currentEntityId}\"`,
                sort: '-created',
                $autoCancel: false
            });
            images = resp?.items ?? [];
        } catch (e) {
            console.error('Error loading gallery:', e);
            error = 'No se pudo cargar la galería';
        } finally {
            isLoading = false;
        }
    }

    async function saveImageUrl(url) {
        if (!currentEntityId || !url) return;
        try {
            await pb.collection(galleryCollection).create({
                [relationField]: currentEntityId,
                [urlField]: url
            }, { $autoCancel: false });
        } catch (e) {
            console.error('Error saving image url:', e);
            if (e?.data) console.error('Validation:', e.data);
            throw e;
        }
    }

    async function addByUrl() {
        if (!newImageUrl?.trim()) return;
        isUploading = true;
        try {
            await saveImageUrl(newImageUrl.trim());
            newImageUrl = '';
            await loadImages();
        } catch (_) {
            error = 'No se pudo agregar la imagen';
        } finally {
            isUploading = false;
        }
    }

    function readFileAsDataUrl(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async function handleFileSelect(event) {
        if (!fileField) {
            error = 'Subida de archivos no configurada. Usa URL.';
            return;
        }
        const files = Array.from(event.target.files || []);
        if (!files.length || !currentEntityId) return;
        
        isUploading = true;
        error = null;
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append(relationField, currentEntityId);
                formData.append(fileField, file);
                
                await pb.collection(galleryCollection).create(formData, { $autoCancel: false });
            }
            await loadImages();
        } catch (e) {
            console.error('Upload failed:', e);
            if (e?.data) console.error('Validation:', e.data);
            error = 'Error subiendo imágenes: ' + (e.message || 'Unknown error');
        } finally {
            isUploading = false;
            event.target.value = '';
        }
    }

    async function handleDrop(event) {
        event.preventDefault();
        if (!canModify && !canCreate) return;
        if (!fileField) {
            error = 'Subida de archivos no configurada. Usa URL.';
            return;
        }
        const dtFiles = Array.from(event.dataTransfer.files || []);
        if (!dtFiles.length) return;
        isUploading = true;
        error = null;
        try {
            for (const file of dtFiles) {
                const formData = new FormData();
                formData.append(relationField, currentEntityId);
                formData.append(fileField, file);
                await pb.collection(galleryCollection).create(formData, { $autoCancel: false });
            }
            await loadImages();
        } catch (e) {
            console.error('Drop upload failed:', e);
            if (e?.data) console.error('Validation:', e.data);
            error = 'Error subiendo imágenes';
        } finally {
            isUploading = false;
        }
    }

    function handleDragOver(event) {
        event.preventDefault();
    }

    async function removeImage(id) {
        if (!id) return;
        try {
            await pb.collection(galleryCollection).delete(id, { $autoCancel: false });
            images = images.filter(img => img.id !== id);
        } catch (e) {
            console.error('Delete failed:', e);
            error = 'No se pudo eliminar la imagen';
        }
    }

    async function confirmDelete(id) {
        try {
            const result = await Swal.fire({
                title: '¿Eliminar imagen?',
                text: 'Esta acción no se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await removeImage(id);
                // Lightbox functionality removed
                await Swal.fire({
                    title: 'Eliminada',
                    text: 'La imagen se eliminó correctamente.',
                    icon: 'success',
                    timer: 1200,
                    showConfirmButton: false
                });
            }
        } catch (e) {
            console.error('Error on confirm delete:', e);
        }
    }
</script>

<!-- no global keyboard listener needed -->

<div class="space-y-4">
    <div class="flex items-center justify-between">
        <h3 class="text-base font-semibold text-blueGray-700 flex items-center gap-2">
            {$t('gallery.gallery')} {entityName ? `de ${entityName}` : ''}
        </h3>
        <div class="flex gap-2 items-center">
            {#if fileField && (canModify || canCreate)}
                <button
                    class="inline-flex items-center gap-2 rounded-md bg-blueGray-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm ring-1 ring-inset ring-blueGray-500/10 hover:bg-blueGray-700 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blueGray-400 active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed transition"
                    on:click={openFileDialog}
                    title={$t('gallery.uploadImages')}
                    aria-label={$t('gallery.uploadImages')}
                    aria-busy={isUploading}
                    disabled={isUploading}
                    type="button"
                >
                    {#if isUploading}
                        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                        <span>{$t('gallery.uploading')}</span>
                    {:else}
                        <i class="fas fa-upload" aria-hidden="true"></i>
                        <span>{$t('gallery.upload')}</span>
                    {/if}
                </button>
                <!-- hidden input used by the header upload button -->
                <input bind:this={headerFileInput} type="file" class="hidden" accept="image/*" multiple on:change={handleFileSelect} disabled={isUploading} />
            {/if}
        </div>
    </div>

    {#if canModify || canCreate}
        <div
            class="rounded-lg border-2 border-dashed border-blueGray-300 p-6 bg-blueGray-50 text-center hover:bg-blueGray-100 transition"
            on:drop={handleDrop}
            on:dragover={handleDragOver}
        >
            <div class="flex flex-col items-center space-y-2">
                <i class="fas fa-cloud-upload-alt text-3xl text-blueGray-400"></i>
                {#if fileField}
                    <p class="text-blueGray-600">{$t('gallery.dragAndDrop')}</p>
                    <label class="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
                        {$t('gallery.selectFiles')}
                        <input type="file" class="hidden" accept="image/*" multiple on:change={handleFileSelect} />
                    </label>
                {:else}
                    <p class="text-blueGray-600">{$t('gallery.directUploadDisabled')}</p>
                {/if}
                {#if isUploading}
                    <div class="text-sm text-blueGray-500">{$t('gallery.uploading')}</div>
                {/if}
            </div>
        </div>

        {#if !fileField || urlField !== fileField}
            <div class="flex gap-2">
                <input
                    type="url"
                    class="flex-1 border border-blueGray-300 rounded px-3 py-2"
                    placeholder={$t('gallery.pasteImageUrl')}
                    bind:value={newImageUrl}
                />
                <button
                    class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                    on:click={addByUrl}
                    disabled={isUploading || !newImageUrl}
                >
                    Agregar URL
                </button>
            </div>
        {/if}
    {/if}

    {#if isLoading}
        <div class="flex justify-center items-center h-24">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blueGray-600"></div>
        </div>
    {:else if error}
        <div class="text-center text-red-500">{error}</div>
    {:else if images.length === 0}
        <div class="text-center text-blueGray-500 p-8">
            <i class="fas fa-images text-4xl mb-4"></i>
            <p>No hay imágenes para este registro.</p>
        </div>
    {:else}
        <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {#each images as img, i}
                {#key img.id}
                <div class="relative group rounded-md overflow-hidden border border-blueGray-100 bg-white">
                    <div class="block w-full">
                        <div class="w-full aspect-square bg-blueGray-100">
                            <img src={fileField && img[fileField] ? pb.files.getUrl(img, img[fileField]) : img[urlField]} alt={newEntity?.nombre || 'Imagen'} class="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" loading="lazy" />
                        </div>
                    </div>
                    {#if canModify || canCreate}
                    <div class="p-1 bg-blueGray-50 border-t border-blueGray-100 flex justify-end">
                        <button class="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded" on:click={() => confirmDelete(img.id)} title={$t('gallery.delete')} aria-label={$t('gallery.delete')}>
                            <i class="fas fa-eraser"></i>
                        </button>
                    </div>
                    {/if}
                </div>
                {/key}
            {/each}
        </div>
    {/if}
</div>


